import {
  artworkShapeProblem,
  audioDurationMs,
  formatBytes,
  playerTime,
  songMediaProblem,
  songMediaUploadType,
  type SongMediaKind,
} from '@church/domain'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { useAccess } from '../access/useAccess.ts'
import { usePreviewPlayer, type PreviewMeta, type PreviewOptions } from './usePreviewPlayer.ts'
import { servicesKeys, type Song } from './useServices.ts'

/**
 * A church's own artwork and full recording for a song (docs/15-MUSIC-AND-AI.md):
 * a Songs Editor's upload and removal, and what every place a song wears its
 * artwork or plays its sound reads — the church's own first, Apple's after.
 * The bytes come through the API (`own.artworkUrl`, `own.audioUrl`): the
 * session decides who gets them.
 */

/** What a song row carries (the list, the picker): its own artwork and recording. */
export interface OwnMediaThumb {
  artworkUrl: string | null
  audioUrl: string | null
  durationMs: number | null
}

export type OwnMediaView = NonNullable<Song['own']>
export type OwnMediaFile = NonNullable<OwnMediaView['artwork']>

/** What a play button plays: the church's own recording, whole, else Apple's 30-second preview. */
export function songSound(
  own: OwnMediaThumb | null | undefined,
  previewUrl: string | null | undefined,
): { url: string | null; options: PreviewOptions } {
  if (own?.audioUrl)
    return {
      url: own.audioUrl,
      options: {
        full: true,
        durationSeconds: own.durationMs !== null ? own.durationMs / 1000 : null,
      },
    }
  return { url: previewUrl ?? null, options: {} }
}

/**
 * "Cover.png · 1200 × 1200 · 2.0 MB" / "Our band.mp3 · 4:05 · 8.4 MB": a
 * file's facts, muted (without its name where the name is already shown).
 */
export function ownFileFacts(file: OwnMediaFile, { withName = true } = {}): string {
  return [
    withName ? file.fileName : null,
    file.width && file.height ? `${file.width} × ${file.height}` : null,
    file.durationMs ? playerTime(file.durationMs / 1000) : null,
    formatBytes(file.sizeBytes),
  ]
    .filter(Boolean)
    .join(' · ')
}

/** "Added by Sam Staffer · Oct 21" */
export function ownFileAdded(file: OwnMediaFile): string {
  const day = new Date(file.uploadedAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
  return file.uploadedBy ? `Added by ${file.uploadedBy} · ${day}` : `Added ${day}`
}

/** What the platform read from a file before it goes: an image's pixels, a recording's length. */
export interface MediaProbe {
  width?: number | null
  height?: number | null
  /** Seconds, as an audio element reports them (Infinity or NaN when it can't tell). */
  durationSeconds?: number | null
}

/** Why a file can't be this song's artwork or recording, before anything is sent — or null. */
export function songMediaRefusal(
  kind: SongMediaKind,
  file: { type: string; name: string; size: number },
  probe: MediaProbe,
): string | null {
  const contentType = songMediaUploadType(kind, file.type, file.name)
  const problem = songMediaProblem(kind, { contentType, sizeBytes: file.size })
  if (problem) return problem
  if (kind === 'artwork' && probe.width != null && probe.height != null)
    return artworkShapeProblem(probe.width, probe.height)
  return null
}

/**
 * Upload (a slot, the bytes straight to storage, confirm) and remove, for
 * each kind; Songs Editors only (cosmetic: the server enforces). The song's
 * page updates from the answer; lists refetch.
 */
export function useSongMediaEditor(songId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const editable = useAccess().hasSomewhere('songs', 'editor')

  const put = (own: OwnMediaView | null) => {
    queryClient.setQueryData<Song>(servicesKeys.song(songId), (song) =>
      song ? { ...song, own } : song,
    )
    // The list, the picker and the suggestions page show it too.
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'songs'] })
  }

  const upload = useMutation({
    mutationFn: async (input: { kind: SongMediaKind; file: File; probe: MediaProbe }) => {
      const { kind, file, probe } = input
      const refusal = songMediaRefusal(kind, file, probe)
      if (refusal) throw new Error(refusal)
      const contentType = songMediaUploadType(kind, file.type, file.name) ?? ''
      const media = api.api.v1.services.songs({ id: songId }).media({ kind })
      const slot = await media['upload-url'].post({
        contentType,
        fileName: file.name,
        sizeBytes: file.size,
      })
      if (slot.error) throw slot.error
      // To the bucket's own signed URL: our session headers have no business there.
      const uploaded = await fetch(slot.data.url, {
        method: 'PUT',
        headers: { 'content-type': contentType },
        body: file,
      })
      if (!uploaded.ok) throw new Error(`The file did not reach storage (${uploaded.status}).`)
      const { data, error } = await media.put({
        key: slot.data.key,
        fileName: file.name,
        ...(kind === 'audio'
          ? { durationMs: audioDurationMs(probe.durationSeconds) }
          : { width: probe.width ?? null, height: probe.height ?? null }),
      })
      if (error) throw error
      return data.own
    },
    onSuccess: put,
  })

  const remove = useMutation({
    mutationFn: async (kind: SongMediaKind) => {
      const { data, error } = await api.api.v1.services
        .songs({ id: songId })
        .media({ kind })
        .delete()
      if (error) throw error
      return data.own
    },
    onSuccess: put,
  })

  return {
    /** Upload artwork or a recording (Songs Editor). */
    editable,
    upload,
    remove,
    /** Which kind is uploading now, for its button's spinner. */
    uploading: upload.isPending ? upload.variables.kind : null,
  }
}

/**
 * The song page's full-track player for the church's own recording: the one
 * shared player, played whole — play / pause, a scrubber, elapsed and total.
 */
export function useOwnRecordingPlayer(song: Pick<Song, 'title' | 'artist' | 'author' | 'own'>) {
  const own = song.own
  const meta: PreviewMeta = {
    title: song.title,
    artist: song.artist ?? song.author ?? '',
    artworkUrl: own?.artworkUrl ?? null,
  }
  const sound = songSound(own, null)
  const player = usePreviewPlayer(sound.url, meta, sound.options)
  return {
    ...player,
    meta,
    /** "1:05" */
    elapsed: playerTime(player.position),
    /** "4:05"; null while its length is not known. */
    total: player.duration > 0 ? playerTime(player.duration) : null,
  }
}
